import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useAuthContext } from '@/features-flat/auth/useAuthContext';
import { useAuth } from '@clerk/clerk-react';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from "@/components/ui/use-toast";
import { Plus, Trash2, Edit, Code } from 'lucide-react';

interface CodeSnippet {
  id: string;
  title: string;
  code: string;
  location: 'head' | 'body' | 'footer';
  created_at: string;
  updated_at: string;
}

interface SnippetFormData {
  title: string;
  code: string;
  location: 'head' | 'body' | 'footer';
}

/**
 * Admin Settings Component
 * 
 * Displays and manages code snippets for injection into head, body, or footer sections
 */
const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading, userRole, hasPermission } = useAuthContext();
  const { getToken } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingSnippets, setIsLoadingSnippets] = useState(false);
  
  // Snippets state
  const [snippets, setSnippets] = useState<CodeSnippet[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingSnippet, setEditingSnippet] = useState<CodeSnippet | null>(null);
  const [formData, setFormData] = useState<SnippetFormData>({
    title: '',
    code: '',
    location: 'head'
  });
  
  const { toast } = useToast();
  
  console.log(`[SettingsPage] Using useAuthContext - isLoading: ${isLoading}, isAuthenticated: ${isAuthenticated}, userRole: ${userRole}`);
  
  // Load all code snippets
  const loadSnippets = async () => {
    if (userRole !== 'admin') return;
    
    setIsLoadingSnippets(true);
    try {
      const token = await getToken();
      console.log('[SettingsPage] Token received from getToken():', token ? `${token.substring(0, 20)}...` : 'null');
      if (!token) {
        throw new Error('Unable to get authentication token');
      }

      console.log('[SettingsPage] Fetching code snippets from D1 database...');
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/settings/code-snippets`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch code snippets');
      }
      
      const data = await response.json() as { success: boolean, snippets?: CodeSnippet[], message?: string };
      if (data.success && data.snippets) {
        setSnippets(data.snippets);
        console.log('[SettingsPage] Code snippets loaded from D1 database:', data.snippets.length);
      } else {
        console.error("Error fetching code snippets from API:", data.message);
      }
    } catch (error) {
      console.error("Error fetching code snippets:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load code snippets.",
      });
    } finally {
      setIsLoadingSnippets(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && !isLoading && userRole === 'admin') {
      loadSnippets();
    }
  }, [isAuthenticated, isLoading, userRole]);
  
  if (isLoading) {
    console.log("[SettingsPage] Rendering Loading...");
    return <div>Loading...</div>;
  }
  
  if (!isAuthenticated) {
    console.log("[SettingsPage] Not authenticated, navigating to login.");
    navigate('/login');
    return null;
  }
  
  if (!user) {
    console.log("[SettingsPage] No user object, rendering loading user info.");
    return <div>Loading user information...</div>;
  }

  // Check if user has admin permissions
  if (userRole !== 'admin' || !hasPermission('access:settings')) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Access Denied</h2>
          <p className="text-muted-foreground">
            You don't have permission to access admin settings.
          </p>
        </div>
      </div>
    );
  }

  const resetForm = () => {
    setFormData({ title: '', code: '', location: 'head' });
    setEditingSnippet(null);
    setShowAddForm(false);
  };

  const handleSaveSnippet = async () => {
    if (!formData.title.trim() || !formData.code.trim()) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Title and code are required.",
      });
      return;
    }

    setIsSaving(true);
    try {
      const token = await getToken();
      console.log('[SettingsPage] Token received from getToken():', token ? `${token.substring(0, 20)}...` : 'null');
      if (!token) {
        throw new Error('Unable to get authentication token');
      }

      const isEditing = editingSnippet !== null;
      const url = isEditing 
        ? `${import.meta.env.VITE_API_BASE_URL}/api/settings/code-snippets/${editingSnippet.id}`
        : `${import.meta.env.VITE_API_BASE_URL}/api/settings/code-snippets`;
      
      const method = isEditing ? 'PUT' : 'POST';
      
      console.log(`[SettingsPage] ${isEditing ? 'Updating' : 'Creating'} code snippet:`, formData.title);
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to save code snippet.' })) as { message?: string };
        throw new Error(errorData.message || 'Failed to save code snippet');
      }

      const result = await response.json() as { success: boolean, message?: string };

      if (result.success) {
        console.log(`[SettingsPage] Code snippet ${isEditing ? 'updated' : 'created'} successfully`);
        toast({ 
          title: "Success", 
          description: result.message || `Code snippet ${isEditing ? 'updated' : 'created'} successfully.`
        });
        
        // Reload snippets and reset form
        await loadSnippets();
        resetForm();
      } else {
        throw new Error(result.message || 'Failed to save code snippet');
      }
    } catch (error: any) {
      console.error('Error saving code snippet:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "An unexpected error occurred.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteSnippet = async (snippet: CodeSnippet) => {
    if (!confirm(`Are you sure you want to delete "${snippet.title}"?`)) {
      return;
    }

    try {
      const token = await getToken();
      console.log('[SettingsPage] Delete token received from getToken():', token ? `${token.substring(0, 20)}...` : 'null');
      if (!token) {
        throw new Error('Unable to get authentication token');
      }

      console.log('[SettingsPage] Deleting code snippet:', snippet.title);
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/settings/code-snippets/${snippet.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to delete code snippet.' })) as { message?: string };
        throw new Error(errorData.message || 'Failed to delete code snippet');
      }

      const result = await response.json() as { success: boolean, message?: string };

      if (result.success) {
        console.log('[SettingsPage] Code snippet deleted successfully');
        toast({ 
          title: "Success", 
          description: "Code snippet deleted successfully."
        });
        
        // Reload snippets
        await loadSnippets();
      } else {
        throw new Error(result.message || 'Failed to delete code snippet');
      }
    } catch (error: any) {
      console.error('Error deleting code snippet:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "An unexpected error occurred.",
      });
    }
  };

  const handleEditSnippet = (snippet: CodeSnippet) => {
    setEditingSnippet(snippet);
    setFormData({
      title: snippet.title,
      code: snippet.code,
      location: snippet.location
    });
    setShowAddForm(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getLocationBadgeColor = (location: string) => {
    switch (location) {
      case 'head': return 'bg-blue-100 text-blue-800';
      case 'body': return 'bg-green-100 text-green-800';
      case 'footer': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Code Snippets Manager</h2>
          <p className="text-muted-foreground">
            Manage code snippets for injection into head, body, or footer sections.
          </p>
        </div>
        <Button 
          onClick={() => setShowAddForm(true)} 
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Snippet
        </Button>
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Code className="h-5 w-5" />
              {editingSnippet ? 'Edit Code Snippet' : 'Add New Code Snippet'}
            </CardTitle>
            <CardDescription>
              {editingSnippet ? 'Update the existing code snippet.' : 'Create a new code snippet to inject into your site.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  placeholder="e.g., Google Analytics, Custom CSS, etc."
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="location">Injection Location</Label>
                <Select value={formData.location} onValueChange={(value) => setFormData(prev => ({ ...prev, location: value as 'head' | 'body' | 'footer' }))}>
                  <SelectTrigger className="bg-white border border-gray-200 text-gray-900">
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="head">Head (&lt;head&gt; section)</SelectItem>
                    <SelectItem value="body">Body (start of &lt;body&gt;)</SelectItem>
                    <SelectItem value="footer">Footer (end of &lt;body&gt;)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="code">Code</Label>
              <Textarea
                id="code"
                placeholder="Paste your code here (e.g., <script>...</script> or <style>...</style>)"
                value={formData.code}
                onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))}
                rows={8}
                className="font-mono text-sm bg-white border border-gray-200 text-gray-900 placeholder-gray-500"
              />
              <div className="text-sm text-muted-foreground">
                {formData.code.length > 0 ? `${formData.code.length} characters` : 'No code entered'}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button onClick={handleSaveSnippet} disabled={isSaving}>
                {isSaving ? 'Saving...' : (editingSnippet ? 'Update Snippet' : 'Save Snippet')}
              </Button>
              <Button variant="outline" onClick={resetForm}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Snippets Table */}
      <Card>
        <CardHeader>
          <CardTitle>Code Snippets Inventory</CardTitle>
          <CardDescription>
            All code snippets stored in D1 database. Changes are immediately available across all pages.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoadingSnippets ? (
            <div className="text-center py-8">Loading snippets...</div>
          ) : snippets.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No code snippets found. Click "Add Snippet" to create your first one.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-200">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">Title</th>
                    <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">Location</th>
                    <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">Code Preview</th>
                    <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">Size</th>
                    <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">Created</th>
                    <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">Updated</th>
                    <th className="border border-gray-200 px-4 py-3 text-center font-semibold text-gray-900">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {snippets.map((snippet) => (
                    <tr key={snippet.id} className="hover:bg-gray-50">
                      <td className="border border-gray-200 px-4 py-3">
                        <div className="font-medium text-gray-900">{snippet.title}</div>
                      </td>
                      <td className="border border-gray-200 px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getLocationBadgeColor(snippet.location)}`}>
                          {snippet.location}
                        </span>
                      </td>
                      <td className="border border-gray-200 px-4 py-3">
                        <div className="bg-white border border-gray-300 rounded p-2 max-w-xs">
                          <pre className="text-xs font-mono whitespace-pre-wrap break-all text-gray-900 overflow-hidden">
                            {snippet.code.substring(0, 100)}
                            {snippet.code.length > 100 && '...'}
                          </pre>
                        </div>
                      </td>
                      <td className="border border-gray-200 px-4 py-3 text-sm text-gray-600">
                        {snippet.code.length} chars
                      </td>
                      <td className="border border-gray-200 px-4 py-3 text-sm text-gray-600">
                        {formatDate(snippet.created_at)}
                      </td>
                      <td className="border border-gray-200 px-4 py-3 text-sm text-gray-600">
                        {formatDate(snippet.updated_at)}
                      </td>
                      <td className="border border-gray-200 px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handleEditSnippet(snippet)}
                            className="flex items-center gap-1"
                          >
                            <Edit className="h-3 w-3" />
                            Edit
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handleDeleteSnippet(snippet)}
                            className="flex items-center gap-1 text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-3 w-3" />
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Database Info */}
      <Card>
        <CardHeader>
          <CardTitle>Database Information</CardTitle>
          <CardDescription>
            Code snippets are stored in Cloudflare D1 database for high performance and global availability.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground space-y-1">
            <p>• Snippets are stored in the D1 database table: <code className="bg-gray-100 px-1 rounded">code_snippets</code></p>
            <p>• Each snippet can target head, body, or footer injection points</p>
            <p>• Changes are immediately available across all pages</p>
            <p>• Database is replicated globally via Cloudflare's edge network</p>
            <p>• Duplicate titles will update existing snippets automatically</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsPage;
